import {
  Box,
  Button,
  Typography,
  Card,
  Grid,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState } from "react";
import { Edit, Delete } from "@mui/icons-material";
import DashboardLayout from "layout";
import AddonsDialog from "components/AddonsDialog";
import { deleteAddon, editAddon, getAddonList } from "services/vehicle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "components/Toast";

interface CreateAddonPayload {
  name: string;
  description: string;
  price: number;
  is_default: boolean;
}

const Addons = () => {
  const { showToast } = useToast();
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState<any>(null);

  const queryClient = useQueryClient();

  const {
    data: addons,
    isLoading,
    refetch,
  } = useQuery<any, any>({
    queryKey: ["addons"],
    queryFn: getAddonList,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAddon,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["addons"] });
      showToast(response?.message || "Add-on deleted successfully", "success");
      setOpenDeleteDialog(false);
    },
    onError: (error: any) => {
      showToast(
        error?.response?.data?.message || "Failed to delete add-on",
        "error",
      );
    },
  });

  const editMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: CreateAddonPayload;
    }) => editAddon(id, payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["addons"] });
      showToast(response?.message || "Add-on updated successfully", "success");
      setOpenAddDialog(false);
    },
    onError: (error: any) => {
      showToast(
        error?.response?.data?.message || "Failed to update add-on",
        "error",
      );
    },
  });

  const handleEdit = (addon: any) => {
    setSelectedAddon(addon);
    setOpenAddDialog(true);
  };

  const handleDelete = (addon: any) => {
    setSelectedAddon(addon);
    setOpenDeleteDialog(true);
  };

  const handleToggleDefault = (addon: any) => {
    editMutation.mutate({
      id: addon._id,
      payload: {
        ...addon,
        is_default: !addon.is_default,
      },
    });
  };

  return (
    <DashboardLayout nextSideBar>
      <Box p={{ xs: 0, sm: 3 }}>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            Add-ons
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpenAddDialog(true)}
            sx={{ bgcolor: "#379E66", "&:hover": { bgcolor: "#2b7d4f" } }}
          >
            Add New Add-on
          </Button>
        </Box>

        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Grid container spacing={3}>
            {addons?.data?.addons?.map((addon: any) => (
              <Grid item xs={12} sm={6} md={4} key={addon._id}>
                <Card sx={{ p: 2, height: "100%" }}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="h6" fontWeight={600}>
                        {addon.name}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(addon)}
                        >
                          <Edit sx={{ fontSize: 20, color: "#667085" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(addon)}
                        >
                          <Delete sx={{ fontSize: 20, color: "#667085" }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography color="#667085">{addon.description}</Typography>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography fontWeight={600} color="#379E66">
                        ₦{addon.price.toLocaleString()}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography color="#667085" fontSize={14}>
                          Default
                        </Typography>
                        <Switch
                          size="small"
                          checked={addon.is_default}
                          // onChange={() => handleToggleDefault(addon)}
                          disabled={editMutation.isPending}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#379E66",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#379E66",
                              },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <AddonsDialog
          open={openAddDialog}
          onClose={() => {
            setOpenAddDialog(false);
            setSelectedAddon(null);
          }}
          editData={selectedAddon}
          onSuccess={refetch}
        />

        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>Delete Add-on</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{selectedAddon?.name}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button
              onClick={() => deleteMutation.mutate(selectedAddon?._id)}
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
        <Box pb={{ xs: 5, sm: 10 }} />
      </Box>
    </DashboardLayout>
  );
};

export default Addons;
